import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { AreaInfographic } from './AreaInfographic';
import { OutdoorsInfographic } from './OutdoorsInfographic';
import { RestaurantsInfographic } from './RestaurantsInfographic';
import { SchoolsInfographic } from './SchoolsInfographic';
import { useImagesLoaded } from '../../../../hooks/useImagesLoaded';
import { COLOR_BLACK, COLOR_WHITE, COLOR_STEAK_SAUCE } from '../../../colors';
import { LL_CIRCULAR_PRO } from '../../../fonts';
import { addCommas } from '../../../../utils';

const SPARKLES_URL = './images/ListingShareable/sparkles.svg';
const KEYFRAME_COUNT = 9;
const KEYFRAME_INTERVAL = 1; // number of frames per keyframe
const HERO_COUNT = 3;
const HERO_KEYFRAME_INTERVAL = (KEYFRAME_COUNT * KEYFRAME_INTERVAL) / HERO_COUNT;
const LAST_KEYFRAME = ((KEYFRAME_COUNT - 1) * KEYFRAME_INTERVAL) + 1;

const COPYRIGHT_YEAR = new Date().getFullYear();
const useStyles = makeStyles((theme)=> ({
    container: {
        display: 'flex',
        height: '100%',
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
        position: 'relative',
    },
    leftWrap: {
        position: 'relative',
        maxWidth: '50%',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: COLOR_WHITE,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        boxSizing: 'border-box',
        padding: 24,
        flex: 1,
    },
    fullWidth: {
        maxWidth: '100%',
    },
    shadowGradient: {
        position: 'absolute',
        width: '100%',
        height: 178,
        bottom: 0,
        left: 0,
        background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%)',
    },
    listingStatusWrap: {
        width: 278,
        height: 104,
        marginBottom: 24,
        marginLeft: -24,
    },
    listingStatusContainer: {
        position: 'relative',
        height: '100%',
        width: 224,
        backgroundColor: COLOR_BLACK,

        '&::before': {
            position: 'absolute',
            right: -52,
            top: 0,
            content: "''",
            height: 104,
            width: 52,
            backgroundColor: COLOR_BLACK,
            borderTopRightRadius: 52,
            borderBottomRightRadius: 52,
        },
    },
    listingStatusLabel: {
        position: 'absolute',
        paddingTop: 38,
        paddingLeft: 24,
        fontSize: 36,
        fontWeight: 800,
        lineHeight: '28px',
        whiteSpace: 'nowrap',
    },
    sparklesLeft: {
        position: 'absolute',
        left: 24,
        bottom: -22,
    },
    sparklesRight: {
        position: 'absolute',
        right: -64,
        top: -16,

    },
    contentWrap: {
        zIndex: 2,
        fontSize: 24,
        fontWeight: 500,
        lineHeight: '32px',
    },
    watermark: {
        position: 'absolute',
        left: '50%',
        bottom: '16%',
        zIndex: 9999,

        '& img': {
            position: 'relative',
            width: 400,
            height: 400,
            left: '-50%',
        },
    },
    complianceWrap: {
        position: 'absolute',
        width: 420,
        bottom: 16,
        right: 15,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    disclaimerWrap: {
        width: '100%',
        color: COLOR_STEAK_SAUCE,
        textAlign: 'right',
        fontSize: '10px',
        lineHeight: '12px',
        letterSpacing: '0.4px',
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
    },
    logoWrapper: {
        marginBottom: '14px',
    },
    lrLogo: {
        height: '12px',
        margin: '0 10px 6px 0',
    },
    logo: {
        marginLeft: '20px',
        height: '24px',
    },
}));

const LABEL_WIDTHS = [
    { pattern: /open/i, width: 236 },
    { pattern: /listed/i, width: 224 },
    { pattern: /contract/i, width: 338 },
    { pattern: /sold/i, width: 292 },
];


export default function Infographic({
    templateContent = {},
    currentFrame = 1,
    onImagesLoaded = null,
}) {
    const classes = useStyles();
    const {
        address,
        city,
        state,
        hero,
        photos,
        area = [],
        outdoors = [],
        restaurants = null,
        schools = [],
        listing_status_label,
        bedrooms,
        bathrooms,
        home_size,
        price_amount,
        open_houses = [],
        watermark = false,
        realtor_logo_required = false,
    } = templateContent || {};
    const sqft = parseInt((home_size || '').replace(/\D/g, ''), 10) > 0 ? parseInt(home_size.replace(/\D/g, ''), 10) : null;
    const photoIndex = Math.floor(currentFrame / HERO_KEYFRAME_INTERVAL);
    const heroImage = (photos && photos.length)
        ? photos[photoIndex < HERO_COUNT ? photoIndex : 0] || photos[photos.length - 1]
        : hero;
    const loadedBgs = useImagesLoaded([heroImage]);
    const openHouses = open_houses || [];
    const listingStatusLabel = listing_status_label || 'JUST LISTED';
    const isSoldLayout = (/sold/i).test(listingStatusLabel);
    const isContractLayout = (/contract/i).test(listingStatusLabel);
    const labelWidth = LABEL_WIDTHS.reduce((result, item)=> {
        if(!result && item.pattern.test(listingStatusLabel)) {
            result = item.width;
        }
        return result;
    }, null);
    if(!heroImage) {
        window.isLoaded = true;
    }

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
        return ()=> {
            window.isLoaded = false;
        };
    }, [loadedBgs, onImagesLoaded]);

    if(area?.length) {
        while(area && area.length < 6) {
            area.push({ category: 'n/a' });
        }
    }

    const display_address = address?.length > 0;

    return (
        <>
            <link rel="stylesheet" href={LL_CIRCULAR_PRO} />
            <div className={classes.container}>
                {/* Left Block */}

                <div className={`${classes.leftWrap} ${isSoldLayout ? classes.fullWidth : ''}`}
                    style={{
                        backgroundImage: `url("${heroImage}")`,
                        width: isSoldLayout ? '100%' : '50%',
                    }}
                >
                    <div className={classes.shadowGradient}></div>
                    <div className={classes.listingStatusWrap}>
                        <div className={classes.listingStatusContainer} style={{width: labelWidth}}>
                            <div className={classes.listingStatusLabel}>{(listing_status_label || 'JUST LISTED').toUpperCase()}</div>
                            <img className={classes.sparklesLeft} src={SPARKLES_URL} alt="sparkles"/>
                            <img className={classes.sparklesRight} src={SPARKLES_URL} alt="sparkles"/>
                        </div>
                    </div>
                    <div className={classes.contentWrap}>
                        {openHouses.length > 0 && (
                            openHouses.map((openHouse)=> {
                                return (
                                    <div key={`${openHouse.date}${openHouse.time}`}>
                                        {openHouse.date} &bull; {openHouse.time}
                                    </div>
                                );
                            })
                        )}

                        {!display_address && city && (
                            <div>in {city}, {state}</div>
                        )}
 
                        {display_address && (
                            address.map((addressLine)=> <div key={addressLine}>{addressLine}</div>)
                        )}      

                        {price_amount && !isSoldLayout && openHouses.length === 0 && (<div>{`$${addCommas(price_amount)}`}</div>)}

                        {openHouses.length === 0 && (<div>
                            {price_amount && isSoldLayout && (<span>{`$${addCommas(price_amount)}`}</span>)}{' '}
                            {price_amount && isSoldLayout && (bedrooms > 0 || bathrooms > 0 || sqft) && (<span>&bull;</span>)}{' '}

                            {bedrooms > 0 && (<span>{bedrooms} bd</span>)}{' '}
                            {bedrooms > 0 && (bathrooms > 0 || sqft) && (<span>&bull;</span>)}{' '}

                            {bathrooms > 0 && (<span>{bathrooms} ba</span>)}{' '}
                            {bathrooms > 0 && (sqft && (<span>&bull;</span>))}{' '}

                            {sqft && (<span>{addCommas(sqft)} sqft</span>)}{' '}
                        </div>)}
                    </div>
                </div>


                {!isSoldLayout && area?.length > 0 && (<AreaInfographic
                    area={area}
                    currentFrame={currentFrame}
                    keyframeInterval={KEYFRAME_INTERVAL}
                    lastKeyframe={LAST_KEYFRAME}
                    isContractLayout={isContractLayout}
                />)}


                {!isSoldLayout && schools?.length > 0 && (<SchoolsInfographic
                    schools={schools}
                />)}

                {!isSoldLayout && outdoors?.length > 0 && (<OutdoorsInfographic
                    outdoors={outdoors}
                />)}

                {!isSoldLayout && restaurants && (<RestaurantsInfographic
                    restaurants={restaurants}
                />)}

                {!isSoldLayout && watermark && (<div className={classes.watermark}>
                    <img src="./images/watermark-stamp.svg" alt="Watermark" />
                </div>)}

                {/* Compliance */}

                <div className={classes.complianceWrap}>
                    <div className={classes.disclaimerWrap}>
                        <div className={classes.logoWrapper}>
                            <img src="./images/lr-logo-disclaimer-grey.svg" className={classes.lrLogo} alt="LR Logo" />
                            {realtor_logo_required && 
                                <img src="./images/realtor-logo-grey.svg" className={classes.logo} alt="Realtor logo" />
                            }
                            <img src="./images/equal-housing-logo-grey.svg" className={classes.logo} alt="Equal Housing logo" />
                        </div>
                        <div>
                            &copy; ListReports {COPYRIGHT_YEAR}, all rights reserved
                        </div>
                        <div>
                            This information is from third-party sources and its accuracy cannot be guaranteed
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
